import {
    Bar,
    CartesianGrid,
    Cell,
    Line,
    LineChart,
    Tooltip,
    XAxis,
    YAxis,
    BarChart,
    ResponsiveContainer,
} from "recharts";

import Typography from "@/ui/atoms/Typography";

import LayoutFillParentCentered from "@/ui/layouts/LayoutFillParentCentered";

import type { DealAnalytics } from "@/ui/organisms/dashboardTypes";
import { CHART_COLORS } from "@/ui/organisms/dashboardTypes";

import { createSortByNumberPropertyComparator } from "@/utils/sort";

import { months } from "../createViewDashboard";

const compareByValueDesc = createSortByNumberPropertyComparator(
    "value",
    "desc",
);

export const retrieveDealAnalyticsNodes = (
    dealAnalytics: DealAnalytics,
    currentMonth: number,
) => {
    const dealsByUser = Object.entries(dealAnalytics.dealsByUser)
        .map(([user, count]) => ({ name: user, value: count }))
        .toSorted(compareByValueDesc);
    const dealsByDatabase = Object.entries(dealAnalytics.dealsByDatabase)
        .map(([database, count]) => ({
            name: database,
            value: count,
        }))
        .toSorted(compareByValueDesc);
    const lineData = Array.from({ length: 12 }, (_, i) => {
        const monthIndex = (currentMonth - (11 - i) + 12) % 12;
        return {
            month: months[monthIndex],
            value: dealAnalytics.dealsOverTime[monthIndex + 1] ?? 0,
        };
    });

    return dealAnalytics.totalDeals > 0
        ? [
              {
                  element: (
                      <>
                          <Typography variant="h1">
                              Total projects: {dealAnalytics.totalDeals}
                          </Typography>
                          <Typography variant="h1">
                              Projects last 24h: {dealAnalytics.createdLast24h}
                          </Typography>
                          <Typography variant="h1">
                              Projects last 7d: {dealAnalytics.createdLast7d}
                          </Typography>
                      </>
                  ),
                  options: {
                      i: "Project Analytics",
                      w: 2,
                      h: 2,
                      x: 0,
                      y: 0,
                  },
              },
              {
                  element: (
                      <>
                          <Typography
                              variant="h1"
                              className="text-center"
                              style={{ height: "10%" }}
                          >
                              Projects by Database ID
                          </Typography>
                          <ResponsiveContainer width="90%" height="80%">
                              <BarChart
                                  data={dealsByDatabase}
                                  width={500}
                                  height={300}
                              >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="name" />
                                  <YAxis allowDecimals={false} />
                                  <Tooltip />
                                  <Bar dataKey="value">
                                      {dealsByDatabase.map((_, index) => (
                                          <Cell
                                              key={`cell-${index}`}
                                              fill={
                                                  CHART_COLORS[
                                                      index %
                                                          CHART_COLORS.length
                                                  ]
                                              }
                                          />
                                      ))}
                                  </Bar>
                              </BarChart>
                          </ResponsiveContainer>
                      </>
                  ),
                  options: {
                      i: "Database Project Chart",
                      w: 2,
                      h: 2,
                      x: 2,
                      y: 0,
                  },
              },
              {
                  element: (
                      <>
                          <Typography
                              variant="h1"
                              className="text-center"
                              style={{ height: "10%" }}
                          >
                              Projects by User ID
                          </Typography>
                          <ResponsiveContainer width="90%" height="80%">
                              <BarChart
                                  data={dealsByUser}
                                  width={500}
                                  height={300}
                              >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="name" />
                                  <YAxis allowDecimals={false} />
                                  <Tooltip />
                                  <Bar dataKey="value">
                                      {dealsByUser.map((_, index) => (
                                          <Cell
                                              key={`cell-${index}`}
                                              fill={
                                                  CHART_COLORS[
                                                      index %
                                                          CHART_COLORS.length
                                                  ]
                                              }
                                          />
                                      ))}
                                  </Bar>
                              </BarChart>
                          </ResponsiveContainer>
                      </>
                  ),
                  options: {
                      i: "User Project Chart",
                      w: 2,
                      h: 2,
                      x: 4,
                      y: 0,
                  },
              },
              {
                  element: (
                      <>
                          <Typography
                              variant="h1"
                              className="text-center"
                              style={{ height: "10%" }}
                          >
                              Projects count
                          </Typography>
                          <ResponsiveContainer width="90%" height="80%">
                              <LineChart data={lineData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="month" />
                                  <YAxis />
                                  <Tooltip />
                                  <Line
                                      type="monotone"
                                      dataKey="value"
                                      stroke="#8884d8"
                                  />
                              </LineChart>
                          </ResponsiveContainer>
                      </>
                  ),
                  options: {
                      i: "Projects over time",
                      w: 6,
                      h: 2,
                      x: 0,
                      y: 2,
                  },
              },
          ]
        : [
              {
                  element: (
                      <LayoutFillParentCentered>
                          <Typography variant="h1">
                              No Projects Found
                          </Typography>
                      </LayoutFillParentCentered>
                  ),
                  options: {
                      i: "No Projects",
                      w: 100,
                      h: 2,
                      x: 0,
                      y: 0,
                      boxed: false,
                      resizable: false,
                      draggable: false,
                  },
              },
          ];
};
